import {
  AddCostElement,
  Categories,
  CostList,
  CostReportDetail,
  CostReportList,
  CreateCategory,
  CreateExam,
  CreateMacroExam,
  CreateRegionalRate,
  CreateWorkstation,
  EditCategory,
  EditCostElement,
  EditExam,
  EditMacroExam,
  EditRegionalRate,
  EditWorkstation,
  Exams,
  MacroExams,
  MedicalServices,
  RegionalRates,
  Workstations
} from 'features/cost-analysis';
import CreateMedicalService from 'features/cost-analysis/medical-services/new/CreateMedicalService';
import {
  ActivitiyList,
  AddExpertise,
  ExpertiseList,
  ExpertiseView,
  RoleList,
  RoleNameList
} from 'features/cost-analysis/team';
import {
  AccessoryCreateView,
  AccessoryListView,
  AccessoryUpdateView,
  ActiveTransportDocumentList,
  AddContract,
  AddInventoryCheck,
  AddMovementReason,
  AddSupplier,
  AddTicket,
  AllQuantities,
  ArchivedMaintenances,
  ArchivedTickets,
  ChecklistTemplatesPage,
  CollapsedMovements,
  ConsumptionDetail,
  Contracts,
  CreateChecklist,
  CreateEquipment,
  CreateLocation,
  CreateProduct,
  CreateSupplierTransportActiveDocument,
  CreateTransportActiveDocument,
  CreateWarehouse,
  DeliveryExpected,
  DocumentsList,
  EditContract,
  EditEquipment,
  EditInventory,
  EditLocation,
  EditMovementReason,
  EditProduct,
  EditSupplier,
  EditWarehouse,
  EquipmentInvoiceDetail,
  EquipmentInvoices,
  Equipments,
  ExpectedEquipmentInvoices,
  ExpiringMaintenances,
  InventoryCheck,
  InventoryCheckArchive,
  InvoicedMovements,
  Locations,
  Maintenance,
  MovementReasons,
  Movements,
  Products,
  ProductsSkuMapping,
  PurchaseAutomaticRequest,
  PurchaseOrder,
  PurchaseOrderList,
  PurchaseRequest,
  PurchaseRequestAutomaticList,
  PurchaseRequestList,
  StockQuantities,
  StockQuantitiesAtDate,
  StockUnderstockProducts,
  SupplierActiveTransportDocumentDetail,
  SupplierActiveTransportDocumentList,
  Suppliers,
  TechnicianCreatedTickets,
  Ticket,
  Tickets,
  ToBeApproveTickets,
  TransportActiveDocumentDetail,
  TransportDocumentDetail,
  TransportDocumentList,
  UnapprovedMovements,
  UnapprovedMovementsDDT,
  UninvoicedMovements,
  Warehouses,
  WithoutDateMaintenances
} from 'features/inventory';
import { CreateTransportDocument, InvoiceDetail, Invoices } from 'features/inventory/invoices';
import { NotificationDetail, NotificationList } from 'features/notifications';
import { ReportList, ReportViewer } from 'features/reporting';
import {
  CreateLISTranscode,
  CreateMedicalServiceEtlFields,
  CreateRequestDepartmentTranscode,
  CreateType,
  DeletedObjectVersionsPage,
  DeletedVersionsPage,
  EditLayer,
  EditLISTranscode,
  EditRequestDepartmentTranscode,
  EditType,
  HistoryList,
  LISTranscodes,
  MedicalServiceEtlFields,
  RequestDepartmentTranscodes,
  SAPSettings,
  TenantSettings,
  Topology,
  TopologyTree,
  UsersManager
} from 'features/settings';
import CostTypes from 'features/settings/cost-types/views/CostTypeListView';
import { ITenantSettings } from 'features/settings/tenant-settings/types';
import MovementVersionsPage from 'features/settings/versions/pages/MovementVersionsPage';
import SapSyncLogPage from 'features/settings/versions/pages/SapSyncLogPage';
import SystemAccessLogPage from 'features/settings/versions/pages/SystemAccessLogPage';
import VersionsPage from 'features/settings/versions/pages/VersionsPage';
import { Public, Tenant } from 'layouts';
import _ from 'lodash';
import { Base, Login, NotFound, TenantHome, UnderDevelop, UnderMaintenance } from 'pages';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth, RequireRoles } from 'shared/permissions';
import { NotOperatorRoles, SuperAdminRoles, TenantRoles } from 'shared/permissions/roles';
import { tenantSettingsService } from 'shared/services';

const AppRoutes = () => {
  const [metabaseTabs, setMetabaseTabs] = useState<string[]>([]);

  useEffect(() => {
    tenantSettingsService
      .get(1)
      .then((res: ITenantSettings) => {
        if (res?.metabase_dashboard_tabs) {
          setMetabaseTabs(
            res.metabase_dashboard_tabs
              .split(',')
              .map((tab) => tab.trim())
              .filter(Boolean)
          );
        }
      })
      .catch(() => {
        // Silently ignore: dynamic routes will not be created
      });
  }, []);

  return (
    <Routes>
      {/* public routes */}
      <Route path="*" element={<NotFound />} />
      <Route path="/" element={<Public />}>
        <Route path="" element={<Base />} />
        <Route path="login" element={<Login />} />
        <Route path="under-develop" element={<UnderDevelop />} />
        <Route path="maintenance" element={<UnderMaintenance />} />
      </Route>

      {/* protected routes */}
      <Route element={<RequireAuth />}>
        <Route element={<RequireRoles allowedRoles={[...TenantRoles, ...SuperAdminRoles]} />}>
          <Route path="tenant" element={<Tenant />}>
            <Route path="under-develop" element={<Navigate to="/under-develop" />} />

            <Route path="" element={<Navigate to="home" />} />
            <Route path="home" element={<TenantHome />} />
            <Route path="users">
              <Route path="" element={<UsersManager />} />
            </Route>
            <Route path="stock">
              <Route path="" element={<StockQuantities />} />
              <Route path="all" element={<AllQuantities />} />
              <Route path="past" element={<StockQuantitiesAtDate />} />
              <Route path="understock" element={<StockUnderstockProducts />} />
            </Route>
            <Route path="contracts">
              <Route path="" element={<Contracts />} />
              <Route path="new" element={<AddContract />} />
              <Route path=":contractId" element={<EditContract />} />
            </Route>
            <Route path="suppliers">
              <Route path="" element={<Suppliers />} />
              <Route element={<RequireRoles allowedRoles={[...NotOperatorRoles]} />}>
                <Route path="new" element={<AddSupplier />} />
                <Route path=":supplierId" element={<EditSupplier />} />
              </Route>
            </Route>
            <Route path="products">
              <Route path="" element={<Products />} />
              <Route element={<RequireRoles allowedRoles={[...NotOperatorRoles]} />}>
                <Route path="new" element={<CreateProduct />} />
                <Route path=":productId" element={<EditProduct />} />
              </Route>
            </Route>
            <Route path="warehouses">
              <Route path="" element={<Warehouses />} />
              <Route path="new" element={<CreateWarehouse />} />
              <Route path=":warehouseId" element={<EditWarehouse />} />
            </Route>
            <Route path="locations">
              <Route path="" element={<Locations />} />
              <Route path="new" element={<CreateLocation />} />
              <Route path=":locationId" element={<EditLocation />} />
            </Route>
            <Route path="equipments">
              <Route path="" element={<Equipments />} />
              <Route element={<RequireRoles allowedRoles={[...NotOperatorRoles]} />}>
                <Route path="new" element={<CreateEquipment />} />
                <Route path=":equipmentId" element={<EditEquipment />} />
                <Route path="invoices">
                  <Route path="" element={<EquipmentInvoices />} />
                  <Route path="all" element={<ExpectedEquipmentInvoices />} />
                  <Route path=":invoiceId" element={<EquipmentInvoiceDetail />} />
                </Route>
              </Route>
              <Route path="accessories">
                <Route path="" element={<AccessoryListView />} />
                <Route path="new" element={<AccessoryCreateView />} />
                <Route path=":accessorId" element={<AccessoryUpdateView />} />
              </Route>
              <Route path="maintenance">
                <Route path="" element={<Maintenance />} />
                <Route path="archive" element={<ArchivedMaintenances />} />
                <Route path="expiring">
                  <Route path="" element={<ExpiringMaintenances />} />
                  <Route path="without-date" element={<WithoutDateMaintenances />} />
                </Route>
                <Route path=":maintenanceId" element={<Maintenance />} />
              </Route>
              <Route path="ticketing">
                <Route path="" element={<Tickets />} />
                <Route path="new" element={<AddTicket />} />
                <Route path="archive" element={<ArchivedTickets />} />
                <Route path="to-approve" element={<ToBeApproveTickets />} />
                <Route path="technician-created" element={<TechnicianCreatedTickets />} />
                <Route path=":ticketId" element={<Ticket />} />
              </Route>
              <Route path="checklist">
                <Route path="" element={<ChecklistTemplatesPage />} />
                <Route path="new" element={<CreateChecklist />} />
              </Route>
            </Route>
            <Route path="categories">
              <Route path="" element={<Categories />} />
              <Route path="new" element={<CreateCategory />} />
              <Route path=":categoryId" element={<EditCategory />} />
            </Route>
            <Route path="types">
              <Route path="" element={<CostTypes />} />
              <Route path="new" element={<CreateType />} />
              <Route path=":typeId" element={<EditType />} />
            </Route>
            <Route path="skus">
              <Route path="" element={<ProductsSkuMapping />} />
              {/* <Route path="new" element={<CreateType />} /> */}
            </Route>
            <Route path="inventory-check">
              <Route path="" element={<InventoryCheck />} />
              <Route path="new" element={<AddInventoryCheck />} />
              <Route path="archive" element={<InventoryCheckArchive />} />
              <Route path=":inventoryId" element={<EditInventory />} />
            </Route>
            <Route path="cost-analysis">
              <Route path="" element={<CostReportList />} />
              {/* <Route path="report-2" element={<CostReport />} /> */}
              <Route path="reports" element={<CostReportDetail />} />
              <Route path="workstations">
                <Route path="" element={<Workstations />} />
                <Route path="new" element={<CreateWorkstation />} />
                <Route path=":workstationId" element={<EditWorkstation />} />
              </Route>
              <Route path="exams">
                <Route path="" element={<Exams />} />
                <Route path="new" element={<CreateExam />} />
                <Route path=":examId" element={<EditExam />} />
              </Route>
              <Route path="macro-exams">
                <Route path="" element={<MacroExams />} />
                <Route path="new" element={<CreateMacroExam />} />
                <Route path=":macroExamId" element={<EditMacroExam />} />
              </Route>
              <Route path="regional-rates">
                <Route path="" element={<RegionalRates />} />
                <Route path="new" element={<CreateRegionalRate />} />
                <Route path=":regionalRateId" element={<EditRegionalRate />} />
              </Route>
              <Route path="costs">
                <Route path="" element={<CostList />} />
                <Route path="add" element={<AddCostElement />} />
                <Route path=":costElementId" element={<EditCostElement />} />
              </Route>
            </Route>
            <Route path="staff-management">
              <Route path="role-names">
                <Route path="" element={<RoleNameList />} />
                <Route path="by-role" element={<RoleList />} />
              </Route>
              <Route path="expertises">
                <Route path="" element={<ExpertiseList />} />
                <Route path="new" element={<AddExpertise />} />
                <Route path=":expertiseId" element={<ExpertiseView />} />
              </Route>
              <Route path="activities" element={<ActivitiyList />} />
            </Route>
            <Route path="reporting" element={<ReportList />}>
              <Route path=":dashboardId" element={<ReportViewer />} />
            </Route>
            <Route path="ddt">
              <Route path="" element={<TransportDocumentList />} />
              <Route path="new" element={<CreateTransportDocument />} />
              <Route path="invoiced-movements" element={<InvoicedMovements />} />
              <Route path=":id" element={<TransportDocumentDetail />} />
            </Route>
            <Route path="active-ddt">
              <Route path="" element={<ActiveTransportDocumentList />} />
              <Route path=":id" element={<TransportActiveDocumentDetail />} />
              <Route path="new" element={<CreateTransportActiveDocument />} />
            </Route>
            <Route path="supplier-active-ddt">
              <Route path="" element={<SupplierActiveTransportDocumentList />} />
              <Route path="new" element={<CreateSupplierTransportActiveDocument />} />
              <Route path=":id" element={<SupplierActiveTransportDocumentDetail />} />
            </Route>

            <Route path="invoices">
              <Route path="" element={<Invoices />} />
              {/* <Route path="invoiced-movements" element={<InvoicedMovements />} /> */}
              <Route path=":invoiceId" element={<InvoiceDetail />} />
            </Route>
            <Route path="medical-services">
              <Route path="" element={<MedicalServices />} />
              <Route path="new" element={<CreateMedicalService />} />
            </Route>
            <Route path="consumption" element={<ConsumptionDetail />} />
            <Route path="movements">
              <Route path="" element={<Movements />} />
              <Route path="detail" element={<CollapsedMovements />} />
              <Route path="unapproved">
                <Route path="" element={<UnapprovedMovementsDDT />} />
                <Route path=":transportDocument" element={<UnapprovedMovements />} />
              </Route>
              <Route path="uninvoiced" element={<UninvoicedMovements />} />
              <Route path="reasons">
                <Route path="" element={<MovementReasons />} />
                <Route path="new" element={<AddMovementReason />} />
                <Route path=":movementReasonId" element={<EditMovementReason />} />
              </Route>
            </Route>
            {/* <Route path="laboratory/assign" element={<AssignLaboratory />} /> */}
            <Route path="orders">
              <Route path="" element={<PurchaseOrderList />} />
              <Route path="delivery" element={<DeliveryExpected />} />
              <Route path="view/:purchaseOrderId" element={<PurchaseOrder />} />
            </Route>
            <Route path="notifications">
              <Route path="" element={<NotificationList />} />
              <Route path="view/:notificationId" element={<NotificationDetail />} />
            </Route>
            <Route path="purchase_requests">
              <Route path="" element={<PurchaseRequestList />} />
              <Route path="automatic" element={<PurchaseRequestAutomaticList />} />
              <Route
                path="automatic/view/:purchaseRequestId"
                element={<PurchaseAutomaticRequest />}
              />
              <Route path="new/" element={<PurchaseRequest />} />
              <Route path="view/:purchaseRequestId" element={<PurchaseRequest />} />
            </Route>
            <Route path="topology">
              <Route path="" element={<Topology />} />
              <Route path=":layer/:id" element={<EditLayer />} />
              <Route path="tree" element={<TopologyTree />} />
            </Route>
            <Route path="settings">
              <Route path="" element={<TenantSettings />} />
              <Route path="sap/" element={<SAPSettings />} />
              {/*
              <Route path="lab-subscriber/">
                <Route path="" element={<LabSubscriberSettings />} />
                <Route path="new" element={<CreateLabSubscriber />} />
                <Route path=":id" element={<EditLabSubscriber />} />
              </Route>
              */}
            </Route>
            <Route path="custom-reports" element={<ReportList category="CUSTOM" />}>
              <Route path=":dashboardId" element={<ReportViewer />} />
            </Route>
            <Route path="documents">
              <Route path="" element={<DocumentsList />} />
            </Route>

            {metabaseTabs.map((context) => {
              return (
                <Route
                  key={context}
                  path={_.kebabCase(context)}
                  element={<ReportList category={context} />}>
                  <Route path=":dashboardId" element={<ReportViewer />} />
                </Route>
              );
            })}

            <Route path="lis-transcodes">
              <Route path="" element={<LISTranscodes />} />
              <Route path="new" element={<CreateLISTranscode />} />
              <Route path=":lisTranscodeId" element={<EditLISTranscode />} />
            </Route>

            <Route path="extra-field-transcode">
              <Route path="" element={<MedicalServiceEtlFields />} />
              <Route path="new" element={<CreateMedicalServiceEtlFields />} />
            </Route>

            <Route path="versions">
              <Route path="" element={<VersionsPage />} />
              <Route path="deleted" element={<DeletedVersionsPage />} />
              <Route path=":model_name/:object_id" element={<DeletedObjectVersionsPage />} />
              <Route path="movement" element={<MovementVersionsPage />} />
              <Route path="logging" element={<SapSyncLogPage />} />
              <Route path="access-logging" element={<SystemAccessLogPage />} />
            </Route>

            <Route path="history">
              <Route path="" element={<HistoryList />} />
            </Route>

            <Route path="department-transcodes">
              <Route path="" element={<RequestDepartmentTranscodes />} />
              <Route path="new" element={<CreateRequestDepartmentTranscode />} />
              <Route path=":typeId" element={<EditRequestDepartmentTranscode />} />
            </Route>
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

export default AppRoutes;
